import { useState, useEffect, useRef } from 'react';
import AdminSidebar from '../../../components/AdminSidebar';
import { getAdminToken } from '../../../lib/adminAuth';

const ORANGE = '#F37428';
const NAVY   = '#0a2540';
const API    = import.meta.env.VITE_API_URL || 'https://masterfileai-api.azurewebsites.net';

function bFetch(path, opts) {
  const token = getAdminToken() || '';
  const sep   = path.includes('?') ? '&' : '?';
  return fetch(`${API}${path}${sep}token=${token}`, {
    method: opts?.method || 'GET',
    headers: { 'Content-Type': 'text/plain' },
    body: opts?.body !== undefined ? JSON.stringify(opts.body) : undefined,
  }).then(async r => {
    const json = await r.json();
    if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
    return json;
  });
}

const MSG_META = {
  fear_forward:           { color: '#ef4444', emoji: '⚠️' },
  ai_forward:             { color: '#6c63ff', emoji: '🤖' },
  operational_efficiency: { color: '#0176CE', emoji: '⚙️' },
  social_proof:           { color: '#10b981', emoji: '🏆' },
  audit_compliance:       { color: '#f59e0b', emoji: '🔍' },
};

const BUTTON_TEXTS = [
  'See How It Works →',
  'Clean My Vendor File →',
  'Start Free Trial →',
  'Get a Free Sample →',
  'Book a Demo →',
  'Learn More →',
];

const DESTINATIONS = [
  { label: 'Landing Page',  description: 'Vendor cleanup page — best for cold outreach', url: 'https://www.masterfileai.com/vendor-file-cleanup' },
  { label: 'Main Website',  description: 'masterfileai.com home',                          url: 'https://www.masterfileai.com' },
  { label: 'Register',      description: 'Sign-up / free trial page',                      url: 'https://app.masterfileai.com/register' },
];

// Client-side HTML builder — mirrors server side
function buildPreviewHtml({ headline, body_copy, bullets, cta_text, cta_url, image_url }) {
  const ctaTxt  = cta_text  || 'See How It Works →';
  const ctaHref = cta_url   || 'https://www.masterfileai.com/vendor-file-cleanup';
  const imgHtml = image_url
    ? `<tr><td style="padding:0;"><img src="${image_url}" alt="" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;" /></td></tr>`
    : '';
  const bodyParas = (body_copy || '').split('\n').filter(l => l.trim())
    .map(l => `<p style="margin:0 0 16px 0;font-size:16px;line-height:26px;color:#4b5563;">${l}</p>`).join('');
  const bulletItems = Array.isArray(bullets) && bullets.length
    ? `<table cellpadding="0" cellspacing="0" style="width:100%;margin:16px 0 20px;background:#f8fafc;border-radius:8px;">
  <tr><td style="padding:16px 20px;">
    ${bullets.map(b => `<table cellpadding="0" cellspacing="0" style="margin-bottom:10px;"><tr>
      <td width="24" valign="top" style="padding-right:10px;font-size:15px;color:#F37428;font-weight:700;line-height:22px;">✓</td>
      <td style="font-size:15px;line-height:22px;color:#374151;">${b}</td>
    </tr></table>`).join('')}
  </td></tr>
</table>` : '';

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>Preview</title></head>
<body style="margin:0;padding:0;background:#f0f2f5;font-family:Arial,Helvetica,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f2f5;padding:24px 0;">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:100%;background:#ffffff;border-radius:12px;overflow:hidden;">
  <tr><td height="5" style="background:#F37428;font-size:5px;line-height:5px;">&nbsp;</td></tr>
  <tr><td style="padding:20px 40px 10px;">
    <table cellpadding="0" cellspacing="0"><tr>
      <td width="32" height="32" style="width:32px;height:32px;background:#F37428;border-radius:7px;text-align:center;vertical-align:middle;line-height:32px;">
        <span style="color:#fff;font-weight:700;font-size:16px;font-family:Arial;">M</span>
      </td>
      <td style="padding-left:10px;color:#111827;font-weight:700;font-size:16px;font-family:Arial;">MasterFile<span style="color:#F37428;">AI</span></td>
    </tr></table>
  </td></tr>
  <tr><td style="padding:16px 40px 4px;">
    <p style="margin:0;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#F37428;font-family:Arial;">VENDOR DATA CLEANUP &bull; MASTERFILEAI</p>
  </td></tr>
  <tr><td style="padding:8px 40px 20px;">
    <h1 style="margin:0;font-size:26px;line-height:34px;color:#111827;font-weight:800;letter-spacing:-0.3px;">${headline || '<span style="color:#d1d5db;">Headline will appear here</span>'}</h1>
  </td></tr>
  ${imgHtml}
  <tr><td style="padding:28px 40px 40px;">
    ${bodyParas || '<p style="color:#d1d5db;font-size:16px;">Body copy will appear here...</p>'}
    ${bulletItems}
    <table cellpadding="0" cellspacing="0" style="margin-top:8px;">
      <tr><td style="border-radius:7px;background:#F37428;">
        <a href="${ctaHref}" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;font-family:Arial;border-radius:7px;">${ctaTxt}</a>
      </td></tr>
    </table>
  </td></tr>
  <tr><td style="padding:20px 40px;background:#f9fafb;border-top:1px solid #f3f4f6;">
    <p style="margin:0;font-size:12px;color:#9ca3af;">© 2026 MasterFileAI &nbsp;·&nbsp; <a href="https://app.masterfileai.com/unsubscribe" style="color:#9ca3af;">Unsubscribe</a></p>
  </td></tr>
</table>
</td></tr></table></body></html>`;
}

function suggestName(typeName, headline) {
  if (!typeName) return '';
  const shortHeadline = headline
    ? headline.replace(/[^a-zA-Z0-9 ]/g, '').split(' ').slice(0, 5).join(' ')
    : '';
  return shortHeadline ? `${typeName} — ${shortHeadline}` : typeName;
}

const STEPS = ['Message Type', 'Copy', 'Image', 'CTA', 'Save'];

export default function TemplateBuilder() {
  const [step, setStep]               = useState(1);
  const [msgTypes, setMsgTypes]       = useState([]);
  const [images, setImages]           = useState([]);
  const [savedTemplates, setSavedTemplates] = useState([]);

  const [selectedType, setSelectedType] = useState(null);
  const [generated, setGenerated]       = useState(null);
  const [generateCount, setGenerateCount] = useState(0);
  const [generating, setGenerating]     = useState(false);
  const [genError, setGenError]         = useState('');
  const [regenLoading, setRegenLoading] = useState({});
  const [hints, setHints]               = useState({});
  const [showHint, setShowHint]         = useState({});

  const [subject, setSubject]           = useState('');
  const [customSubject, setCustomSubject] = useState(false);
  const [headline, setHeadline]         = useState('');
  const [customHeadline, setCustomHeadline] = useState(false);
  const [bodyCopy, setBodyCopy]         = useState('');
  const [customBody, setCustomBody]     = useState(false);
  const [bullets, setBullets]           = useState(['', '', '']);
  const [previewText, setPreviewText]   = useState('');

  const [selectedImage, setSelectedImage] = useState(null);
  const [ctaText, setCtaText]           = useState('See How It Works →');
  const [ctaUrl, setCtaUrl]             = useState('https://www.masterfileai.com/vendor-file-cleanup');
  const [templateName, setTemplateName] = useState('');
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(null);

  const [testEmail, setTestEmail]       = useState('');
  const [testSending, setTestSending]   = useState(false);
  const [testSent, setTestSent]         = useState(false);

  const fileInputRef = useRef(null);
  const [uploading, setUploading]       = useState(false);
  const [showSaved, setShowSaved]       = useState(false);

  // Prompt editor
  const [promptEditing, setPromptEditing] = useState(null); // type object
  const [promptText, setPromptText]       = useState('');
  const [promptSaving, setPromptSaving]   = useState(false);

  function openPromptEditor(e, t) {
    e.stopPropagation();
    setPromptText(t.prompt || '');
    setPromptEditing(t);
  }

  async function savePrompt() {
    if (!promptEditing) return;
    setPromptSaving(true);
    try {
      await bFetch(`/api/console/builder/message-types/${promptEditing.type_key}`, { method: 'PUT', body: { prompt: promptText } });
      setMsgTypes(prev => prev.map(t => t.type_key === promptEditing.type_key ? { ...t, prompt: promptText } : t));
      if (selectedType?.type_key === promptEditing.type_key) setSelectedType(prev => ({ ...prev, prompt: promptText }));
      setPromptEditing(null);
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setPromptSaving(false); }
  }

  useEffect(() => {
    bFetch('/api/console/builder/message-types').then(r => setMsgTypes(r?.items || [])).catch(() => {});
    bFetch('/api/console/builder/images').then(r => setImages(r?.items || [])).catch(() => {});
    bFetch('/api/console/builder/templates').then(r => setSavedTemplates(r?.items || [])).catch(() => {});
  }, []);

  // Auto-suggest name when entering step 5
  useEffect(() => {
    if (step === 5 && !templateName && selectedType && headline) {
      setTemplateName(suggestName(selectedType.type_name, headline));
    }
  }, [step]);

  async function generate() {
    if (!selectedType) return;
    setGenerating(true); setGenError('');
    try {
      const r = await bFetch('/api/console/builder/generate', {
        method: 'POST', body: { message_type_key: selectedType.type_key, regen: generateCount > 0 },
      });
      setGenerateCount(c => c + 1);
      if (!r.subject_lines || !r.headlines || !r.body_copy)
        throw new Error('Unexpected response format — try regenerating');
      setGenerated(r);
      setSubject(r.subject_lines[0] || '');
      setHeadline(r.headlines[0] || '');
      setBodyCopy(r.body_copy[0] || '');
      setBullets(r.bullets?.length ? r.bullets : ['', '', '']);
      setPreviewText(r.preview_text || '');
      setCustomSubject(false); setCustomHeadline(false); setCustomBody(false);
    } catch (e) { setGenError(e.message); }
    finally { setGenerating(false); }
  }

  async function regenSection(section, hint) {
    if (!selectedType) return;
    setRegenLoading(r => ({ ...r, [section]: true }));
    try {
      const body = { message_type_key: selectedType.type_key, section };
      if (hint)             { body.hint = hint; }
      if (section === 'body') { body.context_subject = subject; body.context_headline = headline; }
      const r = await bFetch('/api/console/builder/generate', { method: 'POST', body });
      setGenerated(prev => {
        const next = { ...prev };
        if (section === 'subjects')  next.subject_lines = r.subject_lines;
        if (section === 'headlines') next.headlines = r.headlines;
        if (section === 'body')      next.body_copy = r.body_copy;
        if (section === 'bullets')   next.bullets = r.bullets;
        if (section === 'preview')   next.preview_text = r.preview_text;
        return next;
      });
      if (section === 'subjects'  && !customSubject)  setSubject(r.subject_lines?.[0] || subject);
      if (section === 'headlines' && !customHeadline) setHeadline(r.headlines?.[0] || headline);
      if (section === 'body'      && !customBody)     setBodyCopy(r.body_copy?.[0] || bodyCopy);
      if (section === 'bullets')  setBullets(r.bullets?.length ? r.bullets : bullets);
      if (section === 'preview')  setPreviewText(r.preview_text || previewText);
    } catch (e) { /* silent — don't disrupt current content */ }
    finally { setRegenLoading(r => ({ ...r, [section]: false })); }
  }

  async function uploadImage(file) {
    setUploading(true);
    try {
      const reader = new FileReader();
      const b64 = await new Promise((res, rej) => {
        reader.onload = e => res(e.target.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const r = await bFetch('/api/console/builder/images/upload', {
        method: 'POST',
        body: { filename: file.name, data: b64, content_type: file.type, alt_text: file.name, message_type_key: selectedType?.type_key || null },
      });
      if (r.url) {
        const newImg = { image_id: Date.now(), filename: file.name, blob_url: r.url };
        setImages(imgs => [newImg, ...imgs]);
        setSelectedImage(r.url);
      }
    } catch (e) { alert('Upload failed: ' + e.message); }
    finally { setUploading(false); }
  }

  async function save() {
    if (!templateName.trim()) return;
    setSaving(true);
    try {
      const r = await bFetch('/api/console/builder/templates/save', {
        method: 'POST',
        body: { template_name: templateName, message_type_key: selectedType?.type_key, subject, preview_text: previewText, headline, body_copy: bodyCopy, bullets, cta_text: ctaText, cta_url: ctaUrl, image_url: selectedImage || null },
      });
      setSaved(r.template_id);
      bFetch('/api/console/builder/templates').then(res => setSavedTemplates(res?.items || [])).catch(() => {});
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  async function sendTest() {
    const html = buildPreviewHtml({ headline, body_copy: bodyCopy, bullets, cta_text: ctaText, cta_url: ctaUrl, image_url: selectedImage });
    setTestSending(true); setTestSent(false);
    try {
      await bFetch('/api/console/builder/test-send', {
        method: 'POST', body: { subject, html_body: html, to_email: testEmail || undefined },
      });
      setTestSent(true);
      setTimeout(() => setTestSent(false), 5000);
    } catch (e) { alert('Send failed: ' + e.message); }
    finally { setTestSending(false); }
  }

  function reset() {
    setStep(1); setSelectedType(null); setGenerated(null); setGenError('');
    setSubject(''); setHeadline(''); setBodyCopy(''); setBullets(['','','']); setPreviewText('');
    setSelectedImage(null); setCtaText('See How It Works →');
    setCtaUrl('https://www.masterfileai.com/vendor-file-cleanup');
    setTemplateName(''); setSaved(null);
  }

  const canAdvance = {
    1: !!selectedType,
    2: !!generated && !!subject && !!headline && !!bodyCopy,
    3: true,
    4: !!ctaText && !!ctaUrl,
    5: !!templateName.trim(),
  };

  const previewHtml = buildPreviewHtml({ headline, body_copy: bodyCopy, bullets, cta_text: ctaText, cta_url: ctaUrl, image_url: selectedImage });

  // ── Saved templates view ──────────────────────────────────────────────────
  if (showSaved) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
        <AdminSidebar active="b2b-builder" />
        <main style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Saved Templates</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{savedTemplates.length} templates</div>
            </div>
            <button style={btn.primary} onClick={() => { reset(); setShowSaved(false); }}>+ New Template</button>
          </div>
          {savedTemplates.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 60 }}>No saved templates yet</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
              {savedTemplates.map(t => {
                const meta = MSG_META[t.message_type_key] || { color: ORANGE };
                return (
                  <div key={t.template_id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ height: 4, background: meta.color }} />
                    <div style={{ padding: '16px 20px' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 4 }}>{t.template_name}</div>
                      {t.type_name && <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: meta.color, marginBottom: 8, display: 'block' }}>{t.type_name}</span>}
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}><strong>Subject:</strong> {t.subject}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Updated {new Date(t.updated_on_utc).toLocaleDateString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <AdminSidebar active="b2b-builder" />

      {/* ── Left wizard ── */}
      <div style={{ width: 460, flexShrink: 0, background: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100vh', position: 'sticky', top: 0 }}>

        {/* Header */}
        <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Template Builder</div>
            <button style={{ ...btn.ghost, fontSize: 11 }} onClick={() => setShowSaved(true)}>View saved →</button>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {STEPS.map((label, i) => {
              const n = i + 1;
              const done = n < step; const active = n === step;
              return (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0, background: done ? '#f0fdf4' : active ? ORANGE : '#f1f5f9', color: done ? '#15803d' : active ? '#fff' : '#94a3b8', border: `2px solid ${done ? '#86efac' : active ? ORANGE : '#e2e8f0'}` }}>
                      {done ? '✓' : n}
                    </div>
                    <span style={{ fontSize: 10, color: active ? NAVY : '#94a3b8', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }}>{label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div style={{ width: 10, height: 1, background: '#e2e8f0', flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* STEP 1 */}
          {step === 1 && (
            <div>
              <StepTitle>Choose a message type</StepTitle>
              <StepDesc>The psychological angle that drives the copy. Each type generates completely different content.</StepDesc>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                {msgTypes.map(t => {
                  const meta = MSG_META[t.type_key] || { color: ORANGE, emoji: '📧' };
                  const sel  = selectedType?.type_key === t.type_key;
                  return (
                    <div key={t.type_key} onClick={() => setSelectedType(t)}
                      style={{ border: `2px solid ${sel ? meta.color : '#e2e8f0'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: sel ? `${meta.color}0d` : '#fff', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{meta.emoji}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: sel ? meta.color : NAVY }}>{t.type_name}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{t.description}</div>
                        </div>
                        {sel && <span style={{ color: meta.color, fontWeight: 700 }}>✓</span>}
                      </div>
                      <div style={{ marginTop: 8, textAlign: 'right' }}>
                        <button style={{ background: 'none', border: 'none', fontSize: 11, color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                          onClick={e => openPromptEditor(e, t)}>Edit prompt</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div>
              <StepTitle>Generate & pick your copy</StepTitle>
              <StepDesc>Claude writes options for each section. Pick your favorite or write your own.</StepDesc>

              <div style={{ marginTop: 12, padding: '10px 14px', background: `${MSG_META[selectedType?.type_key]?.color || ORANGE}0d`, border: `1px solid ${MSG_META[selectedType?.type_key]?.color || ORANGE}30`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: MSG_META[selectedType?.type_key]?.color || ORANGE, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{selectedType?.type_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{selectedType?.description}</div>
                </div>
                <button style={{ ...btn.ghost, fontSize: 11, padding: '4px 8px' }} onClick={() => setStep(1)}>Change</button>
              </div>

              <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                <button style={{ ...btn.primary, flex: 1, justifyContent: 'center' }}
                  onClick={generate} disabled={generating}>
                  {generating ? <><Spinner />Generating…</> : generated ? '↻ Regenerate' : '✦ Generate with AI'}
                </button>
              </div>

              {genError && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#dc2626' }}>{genError}</div>
              )}

              {generated && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>Click to select. Use "Write my own" to customize.</div>

                  {(['subjects', 'headlines', 'body']).map(sec => {
                    const labelMap    = { subjects: 'Subject line', headlines: 'Headline', body: 'Intro paragraph' };
                    const options     = sec === 'subjects' ? generated.subject_lines : sec === 'headlines' ? generated.headlines : generated.body_copy;
                    const selected    = sec === 'subjects' ? subject   : sec === 'headlines' ? headline   : bodyCopy;
                    const setSelected = sec === 'subjects' ? setSubject : sec === 'headlines' ? setHeadline : setBodyCopy;
                    const custom      = sec === 'subjects' ? customSubject : sec === 'headlines' ? customHeadline : customBody;
                    const setCustom   = sec === 'subjects' ? setCustomSubject : sec === 'headlines' ? setCustomHeadline : setCustomBody;
                    const placeholder = sec === 'subjects' ? 'Your subject…' : sec === 'headlines' ? 'Your headline…' : 'Your intro paragraph…';
                    return (
                      <PickSection key={sec} label={labelMap[sec]}
                        onRedo={() => setShowHint(h => ({ ...h, [sec]: !h[sec] }))}
                        redoing={regenLoading[sec]}
                        hintBox={showHint[sec] && (
                          <HintBox
                            hint={hints[sec] || ''}
                            onHintChange={v => setHints(h => ({ ...h, [sec]: v }))}
                            onGenerate={() => { regenSection(sec, hints[sec]); setShowHint(h => ({ ...h, [sec]: false })); }}
                            onClose={() => setShowHint(h => ({ ...h, [sec]: false }))}
                            loading={regenLoading[sec]}
                          />
                        )}
                      >
                        {(options || []).map((opt, i) => (
                          <PickOption key={i} selected={selected === opt && !custom}
                            onClick={() => { setSelected(opt); setCustom(false); }}>{opt}</PickOption>
                        ))}
                        {custom
                          ? (sec === 'body'
                              ? <textarea value={selected} onChange={e => setSelected(e.target.value)} placeholder={placeholder} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} autoFocus />
                              : <input value={selected} onChange={e => setSelected(e.target.value)} placeholder={placeholder} style={inputStyle} autoFocus />)
                          : <button style={btn.link} onClick={() => setCustom(true)}>+ Write my own</button>}
                      </PickSection>
                    );
                  })}

                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <label style={{ ...lbl, marginBottom: 0 }}>Bullet checkpoints <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— shown with ✓ in email</span></label>
                      <RedoBtn onClick={() => setShowHint(h => ({ ...h, bullets: !h.bullets }))} loading={regenLoading.bullets} />
                    </div>
                    {showHint.bullets && (
                      <HintBox
                        hint={hints.bullets || ''}
                        onHintChange={v => setHints(h => ({ ...h, bullets: v }))}
                        onGenerate={() => { regenSection('bullets', hints.bullets); setShowHint(h => ({ ...h, bullets: false })); }}
                        onClose={() => setShowHint(h => ({ ...h, bullets: false }))}
                        loading={regenLoading.bullets}
                      />
                    )}
                    {bullets.map((b, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ color: ORANGE, fontWeight: 700, fontSize: 14, width: 18, flexShrink: 0 }}>✓</span>
                        <input value={b} onChange={e => { const next = [...bullets]; next[i] = e.target.value; setBullets(next); }}
                          style={{ ...inputStyle, marginBottom: 0 }} placeholder={`Bullet ${i + 1}…`} />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <label style={{ ...lbl, marginBottom: 0 }}>Preview text <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(shows after subject in inbox)</span></label>
                      <RedoBtn onClick={() => setShowHint(h => ({ ...h, preview: !h.preview }))} loading={regenLoading.preview} />
                    </div>
                    {showHint.preview && (
                      <HintBox
                        hint={hints.preview || ''}
                        onHintChange={v => setHints(h => ({ ...h, preview: v }))}
                        onGenerate={() => { regenSection('preview', hints.preview); setShowHint(h => ({ ...h, preview: false })); }}
                        onClose={() => setShowHint(h => ({ ...h, preview: false }))}
                        loading={regenLoading.preview}
                      />
                    )}
                    <input value={previewText} onChange={e => setPreviewText(e.target.value)}
                      style={inputStyle} placeholder={generated.preview_text || 'Optional…'} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Image library first, upload secondary */}
          {step === 3 && (
            <div>
              <StepTitle>Add a hero image</StepTitle>
              <StepDesc>Pick from your library or upload a new one. Optional — skip for text-only.</StepDesc>

              {/* No image option */}
              <div onClick={() => setSelectedImage(null)}
                style={{ border: `2px solid ${!selectedImage ? ORANGE : '#e2e8f0'}`, borderRadius: 8, padding: '9px 14px', cursor: 'pointer', marginTop: 14, marginBottom: 12, background: !selectedImage ? '#fff7ed' : '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>{!selectedImage ? '✓' : '○'}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: !selectedImage ? ORANGE : '#64748b' }}>No image — text only</span>
              </div>

              {/* Image library */}
              {images.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 12 }}>
                  {images.map(img => (
                    <div key={img.image_id} onClick={() => setSelectedImage(img.blob_url)}
                      style={{ border: `2px solid ${selectedImage === img.blob_url ? ORANGE : '#e2e8f0'}`, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', position: 'relative', background: '#f8fafc' }}>
                      <img src={img.blob_url} alt={img.alt_text || img.filename}
                        style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
                        onError={e => { e.target.style.display = 'none'; }} />
                      {selectedImage === img.blob_url && (
                        <div style={{ position: 'absolute', top: 4, right: 4, background: ORANGE, color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>✓</div>
                      )}
                      <div style={{ padding: '4px 8px', fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.alt_text || img.filename}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              <button style={{ ...btn.secondary, width: '100%', justifyContent: 'center' }}
                onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <><Spinner />Uploading…</> : images.length > 0 ? '⬆ Upload another image' : '⬆ Upload image'}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ''; }} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>Best size: 1200×628px landscape (2× retina for 600px email width)</div>
            </div>
          )}

          {/* STEP 4 — CTA */}
          {step === 4 && (
            <div>
              <StepTitle>Call to action</StepTitle>
              <StepDesc>Choose button text and where readers land when they click.</StepDesc>

              <div style={{ marginTop: 16 }}>
                <label style={lbl}>Button text</label>
                <input value={ctaText} onChange={e => setCtaText(e.target.value)} style={inputStyle} />
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {BUTTON_TEXTS.map(t => (
                    <button key={t}
                      style={{ ...btn.chip, ...(ctaText === t ? { background: ORANGE, color: '#fff', borderColor: ORANGE } : {}) }}
                      onClick={() => setCtaText(t)}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <label style={lbl}>Destination</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  {DESTINATIONS.map(d => (
                    <button key={d.label}
                      onClick={() => setCtaUrl(d.url)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        padding: '10px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                        border: `2px solid ${ctaUrl === d.url ? ORANGE : '#e2e8f0'}`,
                        background: ctaUrl === d.url ? 'rgba(243,116,40,0.06)' : '#fff',
                      }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: ctaUrl === d.url ? ORANGE : NAVY }}>{d.label}</span>
                      <span style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{d.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5 — Name & Save */}
          {step === 5 && (
            <div>
              <StepTitle>Name & save</StepTitle>
              <StepDesc>Name auto-suggested from your type and headline. Edit if needed.</StepDesc>
              <div style={{ marginTop: 16 }}>
                <label style={lbl}>Template name</label>
                <input value={templateName} onChange={e => setTemplateName(e.target.value)}
                  style={inputStyle} autoFocus />
              </div>

              <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', fontSize: 12 }}>
                <SumRow label="Type"    val={selectedType?.type_name} />
                <SumRow label="Subject" val={subject} />
                <SumRow label="Image"   val={selectedImage ? 'Yes' : 'None'} />
                <SumRow label="CTA"     val={ctaText} last />
              </div>

              {saved ? (
                <div style={{ marginTop: 20, padding: '14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>✓ Template saved!</div>
                  <div style={{ fontSize: 11, color: '#166534', marginTop: 3 }}>ID: {saved}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button style={btn.primary} onClick={reset}>+ New template</button>
                    <button style={btn.secondary} onClick={() => { reset(); setShowSaved(true); }}>View all</button>
                  </div>
                </div>
              ) : (
                <button style={{ ...btn.primary, marginTop: 18, width: '100%', justifyContent: 'center', opacity: templateName.trim() ? 1 : 0.4 }}
                  onClick={save} disabled={saving || !templateName.trim()}>
                  {saving ? <><Spinner />Saving…</> : '💾 Save Template'}
                </button>
              )}

              {!saved && (
                <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: NAVY, marginBottom: 10 }}>Send a test email first</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>Verify the image and links look right before saving.</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                      placeholder="you@example.com" style={{ ...inputStyle, flex: 1 }} />
                    <button style={{ ...btn.secondary, flexShrink: 0 }} onClick={sendTest} disabled={testSending}>
                      {testSending ? '…' : testSent ? '✓ Sent!' : 'Send'}
                    </button>
                  </div>
                  {testSent && <div style={{ fontSize: 11, color: '#15803d', marginTop: 6 }}>Check your inbox — image and unsubscribe link should work.</div>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <button style={btn.ghost} onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}>← Back</button>
          {step < 5 && (
            <button style={{ ...btn.primary, opacity: canAdvance[step] ? 1 : 0.4, cursor: canAdvance[step] ? 'pointer' : 'not-allowed' }}
              onClick={() => { if (canAdvance[step]) setStep(s => s + 1); }} disabled={!canAdvance[step]}>
              {step === 2 && !generated ? 'Generate first ↑' : 'Continue →'}
            </button>
          )}
        </div>
      </div>

      {/* ── Prompt editor modal ── */}
      {promptEditing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={() => setPromptEditing(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', width: 680, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Edit Prompt — {promptEditing.type_name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>This prompt instructs the AI how to write copy for this message type. Changes take effect immediately on next generation.</div>
              </div>
              <button style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }} onClick={() => setPromptEditing(null)}>✕</button>
            </div>
            <textarea
              value={promptText}
              onChange={e => setPromptText(e.target.value)}
              style={{ flex: 1, minHeight: 340, border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', fontSize: 12, lineHeight: 1.6, fontFamily: 'monospace', resize: 'vertical', marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' }} onClick={() => setPromptEditing(null)}>Cancel</button>
              <button style={{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: promptSaving ? 0.6 : 1 }} onClick={savePrompt} disabled={promptSaving}>
                {promptSaving ? 'Saving…' : 'Save Prompt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Right: Preview ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#e5e7eb' }}>
        <div style={{ padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Live Preview</div>
          {subject && <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 500 }}><strong style={{ color: '#374151' }}>Subject:</strong> {subject}</div>}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '24px 0', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          <iframe srcDoc={previewHtml} title="Email preview"
            style={{ width: 640, height: 700, border: 'none', borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.1)', background: '#fff' }}
            sandbox="allow-same-origin" />
        </div>
      </div>
    </div>
  );
}

function StepTitle({ children }) { return <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 5 }}>{children}</div>; }
function StepDesc({ children })  { return <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{children}</div>; }

function RedoBtn({ onClick, loading }) {
  return (
    <button onClick={onClick} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', background: loading ? '#f1f5f9' : '#fff7ed', color: ORANGE, border: `1px solid ${ORANGE}50`, borderRadius: 20, fontSize: 10, fontWeight: 700, cursor: loading ? 'default' : 'pointer', flexShrink: 0 }}>
      {loading ? '…' : '✦ Redo'}
    </button>
  );
}

function HintBox({ section, hint, onHintChange, onGenerate, onClose, loading }) {
  return (
    <div style={{ marginTop: 6, marginBottom: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 5, fontWeight: 600 }}>Give the AI a direction for this section:</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={hint}
          onChange={e => onHintChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && hint.trim()) onGenerate(); if (e.key === 'Escape') onClose(); }}
          placeholder={`e.g. "focus on 1099 risk" or "mention SOX audits"…`}
          style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '6px 10px', marginBottom: 0 }}
          autoFocus
        />
        <button onClick={() => hint.trim() && onGenerate()} disabled={loading || !hint.trim()}
          style={{ ...btn.primary, padding: '6px 12px', fontSize: 12, opacity: hint.trim() ? 1 : 0.4 }}>
          {loading ? '…' : 'Go'}
        </button>
        <button onClick={onClose} style={{ ...btn.ghost, padding: '6px 8px', fontSize: 12 }}>✕</button>
      </div>
    </div>
  );
}

function PickSection({ label, children, onRedo, redoing, hintBox }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>{label}</div>
        <div style={{ display: 'flex', gap: 5 }}>
          {onRedo && <RedoBtn onClick={onRedo} loading={redoing} />}
        </div>
      </div>
      {hintBox}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function PickOption({ selected, onClick, children }) {
  return (
    <div onClick={onClick} style={{ border: `2px solid ${selected ? ORANGE : '#e2e8f0'}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', background: selected ? '#fff7ed' : '#fafafa', fontSize: 12, color: selected ? '#92400e' : '#374151', lineHeight: 1.4, transition: 'all 0.1s' }}>
      {selected && <span style={{ fontWeight: 700, marginRight: 5, color: ORANGE }}>✓</span>}
      {children}
    </div>
  );
}

function SumRow({ label, val, last }) {
  return (
    <div style={{ display: 'flex', gap: 10, paddingBottom: last ? 0 : 7, marginBottom: last ? 0 : 7, borderBottom: last ? 'none' : '1px solid #f1f5f9' }}>
      <span style={{ color: '#94a3b8', width: 50, flexShrink: 0, fontSize: 12 }}>{label}</span>
      <span style={{ color: '#374151', fontWeight: 500, flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val || '—'}</span>
    </div>
  );
}

function Spinner() {
  return <span style={{ display: 'inline-block', width: 11, height: 11, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', marginRight: 5 }} />;
}

const lbl = { display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
const btn = {
  primary:   { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  secondary: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: NAVY, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  ghost:     { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', background: 'transparent', color: '#64748b', border: '1px solid transparent', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  link:      { background: 'none', border: 'none', color: ORANGE, fontSize: 12, cursor: 'pointer', padding: '2px 0', textDecoration: 'underline' },
  chip:      { padding: '4px 10px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 20, fontSize: 11, cursor: 'pointer' },
};
